import { useEffect, useState } from 'react';
import { ProvisionApi } from '@Apis';
import AcsRetirementModal from './AcsRetirementModal';
import { ReauthRequiredOrg } from '@Types/provision';

const STORAGE_KEY = 'casAcsModalDismissedDate';

function getTodayDateString(): string {
  const today = new Date();
  return today.toISOString().split('T')[0];
}

function shouldShowModal(): boolean {
  const dismissedDate = localStorage.getItem(STORAGE_KEY);
  const today = getTodayDateString();

  return !dismissedDate || dismissedDate !== today;
}

function recordDismissal(): void {
  const today = getTodayDateString();
  localStorage.setItem(STORAGE_KEY, today);
}

/**
 * ACS (Azure Access Control Service) Retirement Banner
 *
 * Microsoft is retiring Azure Access Control Service (ACS) on April 2, 2026.
 * This banner checks if any organizations require re-authentication and displays
 * a modal to guide users through the token recreation process.
 *
 * Modal display logic:
 * - Only shows if there are affected organizations
 * - Will not show again on the same day after user dismisses it
 * - Will show again the next day
 */
function AcsRetirementBanner() {
  const [reauthRequiredOrgs, setReauthRequiredOrgs] = useState<
    ReauthRequiredOrg[]
  >([]);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!shouldShowModal()) {
      return;
    }

    // Fetch organizations that require re-authentication
    ProvisionApi.getReauthRequiredOrgs()
      .then((res) => {
        const orgs = res.data || [];
        if (orgs.length > 0) {
          setReauthRequiredOrgs(orgs);
          setShowModal(true);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch reauth required orgs:', error);
      });
  }, []);

  const handleClose = () => {
    recordDismissal();
    setShowModal(false);
  };

  if (!showModal || reauthRequiredOrgs.length === 0) {
    return null;
  }

  return (
    <AcsRetirementModal
      reauthRequiredOrgs={reauthRequiredOrgs}
      onClose={handleClose}
    />
  );
}

export default AcsRetirementBanner;
